import { inferredTagsForNote, type ContentType, type Note, type NoteStatus } from '../domain/note'
import { stripMarkdownHighlightMarkers } from '../utils/markdownHighlights'

export interface NoteSearchProjection {
  id: string
  title: string
  body: string
  annotation: string
  tags: string
  domains: string
  linkContext: string
  /** What Unpack read in the note's screenshots (screenshotCleanup/card.ts): searchable like the note's own words, never part of them. */
  unpacked: string
  status: NoteStatus
  contentType: ContentType
  modifiedAt: number
  expiresAt: number | null
  nextReminderAt: number | null
}

/** One projection contract is shared by the immediate and deferred search engines. */
export function createNoteSearchProjection(note: Note, unpacked = ''): NoteSearchProjection {
  const inferredTags = inferredTagsForNote(note)
  return {
    id: note.id,
    title: stripMarkdownHighlightMarkers(note.manualTitle ?? note.derivedTitle),
    body: stripMarkdownHighlightMarkers(note.body),
    annotation: stripMarkdownHighlightMarkers(note.annotation),
    tags: [...new Set([...note.userTags, ...inferredTags])].join(' '),
    domains: note.urls.flatMap((url) => (url.domain ? [url.domain] : [])).join(' '),
    linkContext: note.urls
      .flatMap((url) => [url.title, url.description, url.siteName, url.author].filter(Boolean))
      .join(' '),
    unpacked,
    status: note.status,
    contentType: note.contentType,
    modifiedAt: note.modifiedAt,
    expiresAt: note.expiresAt,
    nextReminderAt: note.reminder?.nextAt ?? null,
  }
}

export function authoredProjectionText(projection: NoteSearchProjection): string {
  return [
    projection.title,
    projection.body,
    projection.annotation,
    projection.tags,
    projection.domains,
    projection.linkContext,
    projection.unpacked,
  ].filter(Boolean).join(' ')
}
